/**
 * Reine Logik-Bausteine für den App-Check-Fallback-Provider.
 *
 * Bewusst frei von Firebase- und Angular-Imports, damit sie in
 * `npm run test:unit` (Vitest, environment `node`) ohne TestBed laufen.
 */

/** Fehler, den {@link withTimeout} bei Fristablauf wirft. */
export class TimeoutError extends Error {
  constructor(label: string, timeoutMs: number) {
    super(`${label} hat nach ${timeoutMs} ms nicht geantwortet`);
    this.name = 'TimeoutError';
  }
}

/**
 * Begrenzt die Wartezeit auf ein Promise.
 *
 * reCAPTCHA bietet keine eigene Timeout-Option: Kommt das Script nicht durch,
 * bleibt `getToken()` hängen, bis das Netz irgendwann aufgibt. Genau das
 * erzeugt die minutenlang „lädt"-Situation bei schlechtem Empfang.
 */
export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  label: string
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new TimeoutError(label, timeoutMs)),
      timeoutMs
    );

    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/**
 * Minimaler Storage-Port für den Circuit Breaker.
 *
 * Bewusst nicht `Storage` aus der DOM-Lib: Die Utils sollen ohne
 * Browser-Typen auskommen und im Node-Environment von Vitest testbar bleiben.
 */
export interface BreakerStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const RECAPTCHA_BREAKER_STORAGE_KEY =
  'awo-care.appcheck.recaptcha-blocked-at';

/**
 * Circuit Breaker für den reCAPTCHA-Pfad.
 *
 * Ist reCAPTCHA im aktuellen Browser blockiert, bleibt es das in aller Regel
 * für die gesamte Sitzung. Ohne Breaker würde jeder Token-Refresh erneut in
 * den vollen Timeout laufen und die App wiederholt einfrieren. Nach dem ersten
 * Fehlschlag geht deshalb für {@link openDurationMs} alles direkt an den
 * Server-Fallback; danach wird reCAPTCHA einmal neu probiert (etwa weil das
 * Funkloch vorbei ist).
 *
 * Der Zustand überdauert optional den Seitenaufruf ({@link BreakerStorage},
 * in der App `sessionStorage`). Ohne das würde jeder Reload erneut in die
 * volle Frist laufen, bevor der Fallback greift — bei dauerhaft blockiertem
 * reCAPTCHA also bei jedem einzelnen Seitenaufruf. Der Rückfallweg ist
 * funktional gleichwertig und schneller, deshalb kostet ein zu früh
 * wiederhergestellter Breaker nichts.
 */
export class RecaptchaCircuitBreaker {
  private openedAt: number | null;

  constructor(
    private readonly openDurationMs: number,
    private readonly now: () => number = () => Date.now(),
    private readonly storage: BreakerStorage | null = null,
    private readonly storageKey: string = RECAPTCHA_BREAKER_STORAGE_KEY
  ) {
    this.openedAt = this.readStored();
  }

  /** true, solange der reCAPTCHA-Pfad übersprungen werden soll. */
  isOpen(): boolean {
    if (this.openedAt === null) {
      return false;
    }

    if (this.now() - this.openedAt >= this.openDurationMs) {
      this.openedAt = null;
      this.clearStored();
      return false;
    }

    return true;
  }

  recordFailure(): void {
    this.openedAt = this.now();
    this.writeStored(this.openedAt);
  }

  recordSuccess(): void {
    this.openedAt = null;
    this.clearStored();
  }

  /**
   * Jeder Zugriff ist gekapselt: In genau den Browsern, um die es hier geht
   * (Tracking-Schutz, Privatmodus), kann bereits `getItem` einen SecurityError
   * werfen. Ein unbrauchbarer Storage darf den Breaker nicht mitreissen.
   */
  private readStored(): number | null {
    if (!this.storage) {
      return null;
    }

    try {
      const raw = this.storage.getItem(this.storageKey);

      if (!raw) {
        return null;
      }

      const openedAt = Number(raw);

      // Ein Zeitpunkt in der Zukunft kann nur von einer verstellten Uhr
      // stammen — sonst bliebe der Breaker dauerhaft offen.
      if (!Number.isFinite(openedAt) || openedAt > this.now()) {
        this.clearStored();
        return null;
      }

      return openedAt;
    } catch {
      return null;
    }
  }

  private writeStored(openedAt: number): void {
    try {
      this.storage?.setItem(this.storageKey, String(openedAt));
    } catch {
      // Ohne Persistenz arbeitet der Breaker rein im Speicher weiter.
    }
  }

  private clearStored(): void {
    try {
      this.storage?.removeItem(this.storageKey);
    } catch {
      // s.o.
    }
  }
}

/**
 * Normalisiert einen beliebigen Fehler auf einen kurzen Code fürs Logging.
 *
 * Firebase-Fehler tragen ihren Code in `error.code` (z.B.
 * `appCheck/recaptcha-error`); alles andere fällt auf den Namen zurück.
 */
export function toErrorCode(error: unknown): string {
  if (error && typeof error === 'object') {
    const code = (error as { code?: unknown }).code;
    if (typeof code === 'string' && code.length > 0) {
      return code;
    }

    const name = (error as { name?: unknown }).name;
    if (typeof name === 'string' && name.length > 0) {
      return name;
    }
  }

  return 'unknown';
}
